import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { useDispatch, useStore } from "react-redux";
import { addBoxThunk, updateBox } from "../../../common/actions.es6";
import { createBox } from "../../../common/common_tools";
import { ID_PREFIX_BOX } from "../../../common/constants";
import { FPS } from "../../../common/sceneTimeline.es6";
import { approveJob } from "../../../common/server_utils.es6";
import { isSlide } from "../../../common/utils";
import { randomPositionGenerator } from "../clipboard/clipboard.utils";
import getMediaBoxSize from "../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../utils/sceneAddSpinner";
import InteractiveCreditButton from "../interactive_item/InteractiveCreditButton";
import {
  SCENE_VISUAL_KINDS,
  convertMotionToVideo,
  editSceneMotion,
  estimateSceneVisual,
  motionEditTarget,
} from "./sceneVisualService";
import "../interactive_item/_interactiveItem.scss";
// MP4 / WebP seçimi Görsel Ekle'nin çıktı düğmeleriyle aynı stil.
import "./_addImageModal.scss";

/* eslint-disable react/prop-types */

/**
 * "AI ile düzenle" — Hareketli Görsel'in kalemi.
 *
 * Seçili kutunun pill menüsünden açılır; iki tür kutuda:
 *   • MotionScene                 → kutunun kendi dokümanı düzenlenir.
 *   • GIF/WebP görseli (bu sekmenin ürettiği) → kutuya eklenirken yazılan
 *     `motionSource` notundaki doküman düzenlenir, döngü yeniden render edilir.
 *
 * Yalnızca bir cümle gider — kare ya da saniye yok. Model sahnenin kendi
 * kodunu okuyup cümlenin adını koyduğu şeyi değiştirir.
 *
 * Sonuç doğrudan kutuya uygulanır (onay kartı yok — kutu zaten sahnede) ve
 * tek bir `updateBox` ile: geri-al tek adımda eski hâle döner, eski dosyalar
 * silinmediği için de çalışır.
 *
 * Görünüm interaktifin "AI ile düzenle"siyle aynı (aynı sınıflar, aynı kredi
 * düğmesi) — aynı ürünün iki yerinde iki farklı düzenleme penceresi olmasın.
 */

export const MOTION_AI_EDIT_EVENT = "aidea:motion-ai-edit";
export const MOTION_TO_VIDEO_EVENT = "aidea:motion-to-video";

const estimateMotion = (fields) => estimateSceneVisual(SCENE_VISUAL_KINDS.MOTION, fields);

function presentOf(store) {
  const s = store.getState();
  return (s && s.undoGroup && s.undoGroup.present) || s || {};
}

/**
 * Tek seferlik bağlanır (EditorApp). Pill ayrı bir portal'da yaşıyor ve kutu
 * seçimi değişince kayboluyor; pencere ise iş bitene kadar dakikalarca
 * açık kalabilmeli — o yüzden pill yalnızca bir olay atar, pencereyi bu açar.
 *
 * İki pencere: "AI ile düzenle" ve "MP4'e çevir".
 */
export default function MotionAiEditHost() {
  const [open, setOpen] = useState(null); // { kind: "edit" | "video", boxId }

  useEffect(() => {
    const opener = (kind) => (e) => {
      const boxId = e && e.detail && e.detail.boxId;
      setOpen(boxId ? { kind, boxId } : null);
    };
    const onEdit = opener("edit");
    const onVideo = opener("video");
    window.addEventListener(MOTION_AI_EDIT_EVENT, onEdit);
    window.addEventListener(MOTION_TO_VIDEO_EVENT, onVideo);
    return () => {
      window.removeEventListener(MOTION_AI_EDIT_EVENT, onEdit);
      window.removeEventListener(MOTION_TO_VIDEO_EVENT, onVideo);
    };
  }, []);

  if (!open) return null;
  const close = () => setOpen(null);
  return open.kind === "video" ? (
    <MotionToVideo key={`video-${open.boxId}`} boxId={open.boxId} onClose={close} />
  ) : (
    <MotionAiEdit key={`edit-${open.boxId}`} boxId={open.boxId} onClose={close} />
  );
}

/**
 * Bir medya kutusunu, kütüphaneden eklenmiş gibi ekler — MyFilesPanel'in
 * yoluyla aynı adımlar: sahnenin kutusuna/slaydına, rastgele konuma, medyanın
 * kendi en-boyuyla, addBoxThunk üzerinden (imleçte, kendi türünün satırına,
 * süresi bilinen klip).
 *
 *   video → EnrichedPlayer, otomatik oynatma açık.
 *   image → HotspotImages (bir kere oynayan WebP), süresi kadar timeline'da.
 *
 * Görsel Ekle'nin "MP4" çıktısı da bunu kullanır (SceneVisualGenerateTab).
 */
export async function addMediaLikeLibrary({ store, dispatch, type, url, navId, durationSec }) {
  const present = presentOf(store);
  const navItem = (present.navItemsById || {})[navId];
  if (!navItem) return false;
  const inASlide = isSlide(navItem.type);
  const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];
  const pluginName = type === "image" ? "HotspotImages" : "EnrichedPlayer";

  const position = {
    x: randomPositionGenerator(10, 30),
    y: randomPositionGenerator(10, 30),
    type: "absolute",
  };
  const boxId = ID_PREFIX_BOX + Date.now();
  spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: false });

  const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, type);
  const initialParams = {
    parent,
    container: 0,
    name: pluginName,
    position,
    id: boxId,
    page: navItem.id,
    url,
    ...(type === "video" ? { initialState: { autoPlay: true } } : {}),
    width,
    height,
    widthUnit,
    heightUnit,
    timeline:
      Number(durationSec) > 0
        ? { durationHintFrames: Math.ceil(Number(durationSec) * FPS), visible: true }
        : undefined,
  };
  createBox(
    initialParams,
    pluginName,
    inASlide,
    (...args) => dispatch(addBoxThunk(...args)),
    present.boxesById || {}
  );
  return true;
}

/**
 * "Çevir" — MotionScene kutusunun dokümanı OLDUĞU GİBİ, kendi süresiyle
 * çevrilir: model yok, yeniden yazma yok, uzatma/kısaltma yok, kredi yok.
 *
 *   MP4 (varsayılan) → video kutusu; imleçle senkron, export'ta doğru.
 *   WebP             → görsel kutusu; bir kere oynayıp son karede durur. Ama
 *                      <img> kendi saatiyle oynar: imleci izlemez, sarılmaz.
 *
 * Bitince sahneye eklenir (motion kutusu yerinde kalır) ve widget'taki kart
 * onaylanır — aynı dosyayı oradan bir daha "Sahneye ekle" ile çift eklemek
 * olmasın.
 */
function MotionToVideo({ boxId, onClose }) {
  const dispatch = useDispatch();
  const store = useStore();
  const [format, setFormat] = useState("mp4");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [source] = useState(() => {
    const present = presentOf(store);
    const toolbar = (present.pluginToolbarsById || {})[boxId];
    const state = (toolbar && toolbar.state) || {};
    if (!toolbar || toolbar.pluginId !== "MotionScene" || !state.htmlUrl) return null;
    return {
      htmlUrl: state.htmlUrl,
      orientation: state.orientation,
      durationSec: state.durationSec,
      // Kutunun sahnesi; yoksa seçili sahne.
      navId: ((present.boxesById || {})[boxId] || {}).parent || present.navItemSelected,
    };
  });

  const run = useCallback(async () => {
    if (!source) return;
    setBusy(true);
    setError(null);
    try {
      const result = await convertMotionToVideo({
        sourceHtmlUrl: source.htmlUrl,
        orientation: source.orientation,
        navId: String(source.navId || ""),
        format,
      });
      const isImage = result && result.kind === "image";
      const url = result && (isImage ? result.imageUrl : result.videoUrl);
      if (!url) throw new Error("no-result");
      const added = await addMediaLikeLibrary({
        store,
        dispatch,
        type: isImage ? "image" : "video",
        url,
        navId: source.navId,
        durationSec: result.durationSec,
      });
      if (!added) throw new Error("no-scene");
      approveJob(result.jobId);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [source, format, store, dispatch, onClose]);

  const seconds = source && Number(source.durationSec) > 0 ? Math.round(source.durationSec) : null;

  return createPortal(
    <div className="ii-editor" onClick={(e) => e.stopPropagation()}>
      <div className="ii-editor__panel ii-editor__panel--narrow">
        <header className="ii-editor__head">
          <div>
            <h2>{i18n.t("SceneVisual.ToVideo", { defaultValue: "MP4 / WebP'ye çevir" })}</h2>
            <p>
              {i18n.t("SceneVisual.ToVideoHint", {
                defaultValue:
                  "Motion olduğu gibi, kendi süresiyle çevrilir ve sahneye eklenir. Model kullanılmaz, kredi düşmez.",
              })}
              {seconds ? ` (${seconds} sn)` : ""}
            </p>
          </div>
          <button type="button" className="ii-editor__x" onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="ii-editor__body">
          {!source && (
            <div className="ii-editor__error">
              {i18n.t("SceneVisual.ToVideoUnavailable", {
                defaultValue: "Bu kutu videoya çevrilemiyor.",
              })}
            </div>
          )}
          {error && <div className="ii-editor__error">{error}</div>}
          {source && (
            <div className="add-image-modal__ai-prompt-toggle" style={{ marginBottom: 0 }}>
              <button
                type="button"
                className={format === "mp4" ? "active" : ""}
                onClick={() => setFormat("mp4")}
                disabled={busy}
              >
                MP4
              </button>
              <button
                type="button"
                className={format === "webp" ? "active" : ""}
                onClick={() => setFormat("webp")}
                disabled={busy}
              >
                WebP
              </button>
            </div>
          )}
          {source && (
            <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
              {format === "webp"
                ? i18n.t("SceneVisual.ToVideoWebpHint", {
                    defaultValue:
                      "Görsel olarak eklenir, bir kere oynayıp son karede durur. Timeline imlecini izlemez: durdurunca ya da sarınca kendi başına oynamaya devam eder.",
                  })
                : i18n.t("SceneVisual.ToVideoMp4Hint", {
                    defaultValue:
                      "Video olarak eklenir, otomatik oynatma açık. Timeline ile senkron oynar.",
                  })}
            </div>
          )}
          {busy && (
            <div className="ii-pane__busy">
              <div className="ii-pane__spinner" />
              <div>
                {format === "webp"
                  ? i18n.t("SceneVisual.ToWebpRunning", { defaultValue: "WebP'ye çevriliyor" })
                  : i18n.t("SceneVisual.ToVideoRunning", { defaultValue: "Videoya çevriliyor" })}
              </div>
              <small>
                {i18n.t("SceneVisual.ToVideoRunningMeta", {
                  defaultValue:
                    "Birkaç dakika sürebilir — pencereyi kapatabilirsin, bitince sahneye eklenir.",
                })}
              </small>
            </div>
          )}
        </div>

        <footer className="ii-editor__foot">
          <button type="button" className="ii-btn" onClick={onClose}>
            {i18n.t("cancel") || "Vazgeç"}
          </button>
          <button
            type="button"
            className="ii-btn ii-btn--primary"
            onClick={run}
            disabled={!source || busy}
          >
            {i18n.t("SceneVisual.ToVideoApply", { defaultValue: "Çevir" })}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

function MotionAiEdit({ boxId, onClose }) {
  const dispatch = useDispatch();
  const store = useStore();
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Açılış anındaki kutu: düzenleme bunun üzerine kurulur.
  const [target] = useState(() =>
    motionEditTarget((presentOf(store).pluginToolbarsById || {})[boxId])
  );
  const isLoop = target && target.kind === "loop";
  const format = isLoop ? (target.output === "gif" ? "GIF" : "WebP") : null;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const run = useCallback(async () => {
    if (!instruction.trim() || !target) return;
    setBusy(true);
    setError(null);
    try {
      const result = await editSceneMotion({
        instruction: instruction.trim(),
        sourceHtmlUrl: target.sourceHtmlUrl,
        output: target.output,
        orientation: target.orientation,
        llmModel: target.llmModel,
        // Yalnızca dosya adının kökü: kutunun sahnesi, yoksa seçili sahne.
        navId: String(
          ((presentOf(store).boxesById || {})[boxId] || {}).parent ||
            presentOf(store).navItemSelected ||
            ""
        ),
      });

      // Pencere kapatılmış ya da kutu silinmiş olabilir — iş dakikalar sürdü.
      // Kutu artık yoksa yazacak yer yok.
      const toolbar = (presentOf(store).pluginToolbarsById || {})[boxId];
      if (!toolbar) return;
      const state = toolbar.state || {};

      let next = null;
      if (target.kind === "motion" && result && result.htmlUrl) {
        next = { ...state, htmlUrl: result.htmlUrl };
      } else if (target.kind === "loop" && result && result.imageUrl) {
        next = {
          ...state,
          url: result.imageUrl,
          // Bir sonraki düzenleme DÜZENLENMİŞ dokümanın üzerine kurulsun.
          motionSource: {
            ...(state.motionSource || {}),
            htmlUrl: result.htmlUrl,
            imageUrl: result.imageUrl,
          },
        };
      }
      if (!next) throw new Error("no-result");
      dispatch(updateBox(boxId, "", toolbar, next));
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [instruction, target, store, dispatch, boxId, onClose]);

  return createPortal(
    <div className="ii-editor" onClick={(e) => e.stopPropagation()}>
      <div className="ii-editor__panel ii-editor__panel--narrow">
        <header className="ii-editor__head">
          <div>
            <h2>{i18n.t("SceneVisual.AiEdit", { defaultValue: "AI ile düzenle" })}</h2>
            <p>
              {isLoop
                ? i18n.t("SceneVisual.AiEditHintLoop", {
                    format,
                    defaultValue:
                      "Ne değişsin yaz. Değişiklik animasyona uygulanır ve {{format}} yeniden oluşturulur.",
                  })
                : i18n.t("SceneVisual.AiEditHint", {
                    defaultValue:
                      "Ne değişsin yaz. Değişiklik mevcut hareketli görselin üzerine uygulanır.",
                  })}
            </p>
          </div>
          <button type="button" className="ii-editor__x" onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="ii-editor__body">
          {!target && (
            <div className="ii-editor__error">
              {i18n.t("SceneVisual.AiEditUnavailable", {
                defaultValue:
                  "Bu görsel AI ile düzenlenemiyor (kaynağı yok). Yeni bir hareketli görsel üretebilirsin.",
              })}
            </div>
          )}
          {error && <div className="ii-editor__error">{error}</div>}

          <textarea
            ref={inputRef}
            className="ii-ai__input"
            rows={3}
            value={instruction}
            disabled={!target || busy}
            placeholder={i18n.t("SceneVisual.AiEditPlaceholder", {
              defaultValue: "Örn: daireyi küçült · yazıyı kırmızı yap · arka planı koyulaştır",
            })}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                run();
              }
            }}
          />

          {busy && (
            <div className="ii-pane__busy">
              <div className="ii-pane__spinner" />
              <div>
                {i18n.t("SceneVisual.AiEditRunning", { defaultValue: "Değişiklik uygulanıyor" })}
              </div>
              <small>
                {i18n.t("SceneVisual.AiEditRunningMeta", {
                  defaultValue:
                    "Birkaç dakika sürebilir — pencereyi kapatabilirsin, bitince kutuya uygulanır.",
                })}
              </small>
            </div>
          )}
        </div>

        <footer className="ii-editor__foot">
          {/* Düzenleme üretim kadar ücretli — rakam görünmeli. */}
          <div style={{ marginRight: "auto" }}>
            <InteractiveCreditButton
              llmModel={target && target.llmModel}
              disabled={busy || !target}
              estimate={estimateMotion}
              label={i18n.t("SceneVisual.AiEdit", { defaultValue: "AI ile düzenle" })}
            />
          </div>
          <button type="button" className="ii-btn" onClick={onClose}>
            {i18n.t("cancel") || "Vazgeç"}
          </button>
          <button
            type="button"
            className="ii-btn ii-btn--primary"
            onClick={run}
            disabled={!target || busy || !instruction.trim()}
          >
            {i18n.t("SceneVisual.AiEditApply", { defaultValue: "Uygula" })}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
